/**
 * @mentions inside plain-text comment bodies.
 *
 * A mention is stored in the body as a `{user:<uuid>}` token — the same
 * `{key:id}` convention the Lexical mention plugin serializes to — so the
 * persisted text never carries a display name that can go stale when a user is
 * renamed. Readers resolve the id to a current name at render time.
 */

const UUID_SOURCE =
  "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}";

function mentionTokenPattern(): RegExp {
  return new RegExp(`\\{user:(${UUID_SOURCE})\\}`, "g");
}

export type CommentBodySegment =
  { kind: "text"; text: string } | { kind: "mention"; userId: string };

export function formatUserMentionToken(userId: string): string {
  return `{user:${userId.toLowerCase()}}`;
}

/** Distinct mentioned user ids, lowercased, in first-appearance order. */
export function extractMentionedUserIds(body: string): string[] {
  const ids = new Set<string>();
  for (const match of body.matchAll(mentionTokenPattern())) {
    const userId = match[1];
    if (userId) ids.add(userId.toLowerCase());
  }
  return [...ids];
}

/** Splits a body into literal text and mention segments, in order. */
export function splitCommentBody(body: string): CommentBodySegment[] {
  const segments: CommentBodySegment[] = [];
  let cursor = 0;
  for (const match of body.matchAll(mentionTokenPattern())) {
    const userId = match[1];
    const start = match.index;
    if (!userId || start === undefined) continue;
    if (start > cursor) {
      segments.push({ kind: "text", text: body.slice(cursor, start) });
    }
    segments.push({ kind: "mention", userId: userId.toLowerCase() });
    cursor = start + match[0].length;
  }
  if (cursor < body.length) {
    segments.push({ kind: "text", text: body.slice(cursor) });
  }
  return segments;
}

/**
 * Renders a body as readable plain text (`@Jane Doe`) for surfaces that cannot
 * show a mention chip — notification previews, emails, agent context.
 */
export function commentBodyToPlainText(
  body: string,
  resolveName: (userId: string) => string | undefined,
  unknownName = "Unknown user",
): string {
  return splitCommentBody(body)
    .map((segment) =>
      segment.kind === "text"
        ? segment.text
        : `@${resolveName(segment.userId) ?? unknownName}`,
    )
    .join("");
}
